
import { useNavigate } from 'react-router-dom'
import { Radio, Text, VStack } from '@chakra-ui/react'
import { useDisplayModal, usePayment, useVehicles } from 'context'
import { VehicleIdentifierType } from 'context/types'
import { CHECK_PLATE_NUMBER_SCHEMA } from 'context/validations'
import { Formik } from 'formik'

import { CardBody, CardContainer } from 'components/CustomCard'
import CustomDivider from 'components/CustomDivider'
import { CustomModal } from 'components/CustomModal'
import { InputControl, PinInputControl, SubmitButton } from 'components/InputCustom'
import RadioControl from 'components/InputCustom/RadioControl'
import { customToast } from 'components/ToastCustom'

export const AddVehicleWithPlateNumber = () => {
  const navigate = useNavigate()
  const { openDialog, closeModal } = useDisplayModal()
  const { getCheckIdentifier, setVehicle, vehicleState: { loading, state: { vehicles } } } = useVehicles()
  const { paymentState: { state: { paymentOptions: { data: { anualPayment, firstPayment } } } } } = usePayment()

  const initialValues: VehicleIdentifierType = {
    radioGroup: 'plateNumber',
    vin: '',
    plateNumber: '',
  }

  const handleGoBack = () => {
    navigate('/my-vehicles')
  }

  const handleSharedPlateNumber = () => {
    openDialog({
      content: (
        <CustomModal
          title={'¡Este vehículo te lo compartieron!'}
          message={'Para renovar la suscripción de este vehículo deberá hacerlo el propietario del mismo.'}
          closeGoBackPress={closeModal}
          closeAcceptPress={closeModal}
          primaryButton />
      ),
      variant: 'primary',
    })
  }

  const handleOwnerScreen = (vin: string) => {
    const vehicle = vehicles.find(x => x.vin.toUpperCase() === vin.toUpperCase())

    if (vehicle) {
      setVehicle(vehicle)
      navigate('/my-vehicles')
    }
  }

  const handleNotAssociated = (vin: string) => {
    openDialog({
      content: (
        <CustomModal
          title={'¡Patente ingresada ya registrada!'}
          message={'La patente que ingresaste se encuentra asociada a otra cuenta. Para asociarla a tu cuenta un'
            + ' ejecutivo deberá validar documentos que podrás adjuntar a continuación.'}
          primaryButton
          closeGoBackPress={() => {
            closeModal()
          }}
          closeAcceptPress={() => {
            closeModal()
            navigate('/owner-validation', { state: { vin } })
          }} />
      ),
      variant: 'primary',
    })
  }

  const handleOnSubmit = async (data: VehicleIdentifierType) => {
    const identifier = data.radioGroup === 'vin' ? data.vin : data.plateNumber

if(identifier) {
  const { msg, isDevice, vin } = await getCheckIdentifier({ identifier })

  switch (msg) {
    case 'VEHICLE_NOT_FOUND':
      return navigate('/hire-wicar', {
        state: {
          subscription: {
            subscriptionType: isDevice
              ? anualPayment.docType ?? 'SUSCR-RENOV'
              : firstPayment.docType ?? 'SUSCR-PRIMER',
            vin
          }
        }
      })
    case 'OWNER':
      return handleOwnerScreen(vin)
    case 'NOT_ASSOCIATED':
      return handleNotAssociated(vin)
    case 'SHARED':
      return handleSharedPlateNumber()
    case 'ASSOCIATED':
      return navigate('/hire-wicar', {
        state: {
          subscription: {
            subscriptionType: isDevice
              ? anualPayment.docType ?? 'SUSCR-RENOV'
              : firstPayment.docType ?? 'SUSCR-PRIMER',
            vin
          }
        }
      })
    case 'CONSUMER_NOT_FOUND':
      return customToast.showToast({
        description: 'Lo sentimos, no pudimos validar tu usuario, por favor contáctate'
          + ' con nosotros. contacto@wicar.cl'
      })
    case 'VIN_NOT_FOUND':
      return customToast.showToast({
        description: 'Hubo un error al validar la información de tu vehículo, por favor ponte en contacto' +
          ' con nosotros',
      })
    default:
      return customToast.showToast({
        description: msg ?? 'Hubo un error al validar su patente, por favor intente nuevamente',
      })
  }
}

  }

  return (
    <Formik
      enableReinitialize
      validateOnMount
      initialValues={initialValues}
      onSubmit={handleOnSubmit}
      validationSchema={CHECK_PLATE_NUMBER_SCHEMA}>
      {({ values, isValid, resetForm }) => (
        <CardContainer isScreen hiddenStack={false} onClose={handleGoBack} headerText={'Datos de vehículo'}>
          <CardBody>
            <VStack w={'full'} justifyContent={'center'} alignItems={'center'} spacing={4}>
              <Text textAlign={'center'} variant={{ base: 'SM', md: 'LG' }}>
                Selecciona la opción que quieres ingresar
              </Text>
              <RadioControl
                w={'full'}
                onClick={() => resetForm()}
                name="radioGroup"
                stackProps={{ alignItems: 'flex-start' }}>
                <Radio value="plateNumber">Patente</Radio>
                <CustomDivider w={'full'} />
                <Radio value="vin">VIN</Radio>
                <CustomDivider w={'full'} />
              </RadioControl>
              {
                values.radioGroup === 'vin'
                  ? <VStack w={'full'} alignItems={'center'} justifyContent={'center'} spacing={6}>
                    <VStack w={'full'}>
                      <Text variant={{ base: 'XS', md: 'MD' }}>
                        Ingresa los 17 dígitos de tu código VIN. Este se encuentra ubicado en los marcos de las puertas
                        delanteras o en el parabrisas de tu vehículo
                      </Text>
                    </VStack>
                    <InputControl
                      inputProps={{ placeholder: 'INGRESAR VIN', maxLength: 17, placeContent: 'center' }}
                      name='vin'
                      isDisabled={loading}
                    />
                  </VStack>
                  : <VStack w={'full'} spacing={6}>
                    <Text variant={{ base: 'XS', md: 'MD' }}>
                      Ingresa los 6 dígitos de la patente de tu vehículo
                    </Text>
                    <PinInputControl
                      pinAmount={6}
                      pinInputProps={{ mask: false, isDisabled: loading, type: 'alphanumeric' }}
                      name='plateNumber'
                      placeholder='INGRESAR PATENTE'
                      label='' />
                  </VStack>
              }
              <SubmitButton
                disabled={values.radioGroup === 'plateNumber'
                  ? (!(isValid && values.plateNumber.length > 5) || loading)
                  : (!(isValid && values.vin.length > 1) || loading)}
                loadingText={'Buscando información...'}
                isLoading={loading}
                mx={'auto'}>
                Siguiente
              </SubmitButton>
            </VStack>
          </CardBody>
        </CardContainer>
      )
      }
    </Formik >
  )
}
